/**
 * Simplified Service Management Tab
 * シンプルなサービス管理タブ
 *
 * 通常メニューとクーポンメニューの2種類管理
 */

import React, { useState, memo, useRef } from 'react';
import { useStore } from '../../../store/useStore';
import {
  Box,
  Typography,
  Button,
  Card,
  ToggleButtonGroup,
  ToggleButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
  LocalOffer as CouponIcon,

  CloudDownload as DownloadIcon,
  CloudUpload as UploadIcon,
  Description as TemplateIcon,
} from '@mui/icons-material';
import Papa from 'papaparse';

// Type imports
import type { ReactChangeEvent } from '../../../types/common.types';
import type { Service } from '../../../types/service';

// CSV Helper functions and types
import {
  downloadCSV,
  serviceToNormalCSV,
  serviceToCouponCSV,
  normalCSVToService,
  couponCSVToService,
  validateNormalCSV,
  validateCouponCSV,
  getNormalMenuTemplate,
  getCouponMenuTemplate,
  type NormalMenuCSV,
  type CouponMenuCSV,
} from '../../../utils/csvHelpers';

// ==========================================
// Type Definitions
// ==========================================

/**
 * Service form data type
 */
type ServiceFormData = Partial<Service>;

/**
 * Component props
 */
interface SimplifiedServiceManagementTabProps {
  services: Service[];
  updateSettings: (section: string, updates: any) => Promise<void>;
  onServiceUpdate?: (services: Service[]) => void;
}

/**
 * Service validation result
 */
interface ServiceValidationResult {
  isValid: boolean;
  errors: string[];
}

// ==========================================
// Helper Functions
// ==========================================







// ==========================================
// Constants
// ==========================================

/**
 * Service categories
 */
const SERVICE_CATEGORIES = [
  { id: 'haircut', name: 'カット', color: '#2196F3' },
  { id: 'coloring', name: 'カラー', color: '#FF9800' },
  { id: 'perm', name: 'パーマ', color: '#9C27B0' },
  { id: 'treatment', name: 'トリートメント', color: '#4CAF50' },
  { id: 'spa', name: 'スパ', color: '#00BCD4' },
  { id: 'other', name: 'その他', color: '#757575' },
];

/**
 * Duration options (in minutes)
 */
const DURATION_OPTIONS = [30, 45, 60, 90, 120, 150, 180, 240];

// ==========================================
// Main Component
// ==========================================

/**
 * SimplifiedServiceManagementTab component
 */
export const SimplifiedServiceManagementTab: React.FC<SimplifiedServiceManagementTabProps> = memo(({
  services: servicesProps,
  updateSettings,
  onServiceUpdate,
}) => {

  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Store state
  const { setServices } = useStore();
  
  // Use services from props instead of store
  const services = servicesProps;
  
  // Local state
  const [menuType, setMenuType] = useState<'normal' | 'coupon'>('normal');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [formData, setFormData] = useState<ServiceFormData>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // ==========================================
  // Event Handlers
  // ==========================================

  /**
   * Handle form field change
   */
  const handleFieldChange = (field: keyof Service, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Handle file upload
   */
  const handleFileUpload = (event: ReactChangeEvent) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    setLoading(true);
    setValidationErrors([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          if (menuType === 'normal') {
            const csvData = results.data as NormalMenuCSV[];
            const allErrors: string[] = [];
            
            // 各行をバリデーション
            csvData.forEach((item, index) => {
              const errors = validateNormalCSV(item);
              if (errors.length > 0) {
                allErrors.push(`行${index + 1}: ${errors.join(', ')}`);
              }
            });
            
            if (allErrors.length > 0) {
              setValidationErrors(allErrors);
              setLoading(false);
              return;
            }
            
            // 各行を変換してサービス配列を作成
            const newServices = csvData.map(item => normalCSVToService(item)) as Service[];
            const updatedServices = [...services.filter(s => s.type !== 'normal'), ...newServices];
            setServices(updatedServices);
            updateSettings('services', updatedServices);
          } else {
            const csvData = results.data as CouponMenuCSV[];
            const allErrors: string[] = [];
            
            // 各行をバリデーション
            csvData.forEach((item, index) => {
              const errors = validateCouponCSV(item);
              if (errors.length > 0) {
                allErrors.push(`行${index + 1}: ${errors.join(', ')}`);
              }
            });
            
            if (allErrors.length > 0) {
              setValidationErrors(allErrors);
              setLoading(false);
              return;
            }
            
            // 各行を変換してサービス配列を作成
            const newServices = csvData.map(item => couponCSVToService(item)) as Service[];
            const updatedServices = [...services.filter(s => s.type !== 'coupon'), ...newServices];
            setServices(updatedServices);
            updateSettings('services', updatedServices);
          }
          
          setUploadDialogOpen(false);
          // onServiceUpdate will be called from updateSettings
        } catch (error) {
          setValidationErrors(['CSVファイルの処理中にエラーが発生しました']);
        } finally {
          setLoading(false);
        }
      },
      error: (error) => {
        setValidationErrors([`CSVファイルの読み込みに失敗しました: ${error.message}`]);
        setLoading(false);
      },
    });
  };

  /**
   * Handle download template
   */
  const handleDownloadTemplate = () => {
    if (menuType === 'normal') {
      const template = getNormalMenuTemplate();
      downloadCSV([template] as unknown[], `normal_menu_template_${new Date().toISOString().split('T')[0]}.csv`);
    } else {
      const template = getCouponMenuTemplate();
      downloadCSV([template] as unknown[], `coupon_menu_template_${new Date().toISOString().split('T')[0]}.csv`);
    }
  };

  /**
   * Handle download current data
   */
  const handleDownloadData = () => {
    const filteredServices = services.filter(s => s.type === menuType) as Service[];
    
    if (menuType === 'normal') {
      const csvData = serviceToNormalCSV(filteredServices);
      downloadCSV(csvData as unknown as unknown[], `normal_menu_${new Date().toISOString().split('T')[0]}.csv`);
    } else {
      const csvData = serviceToCouponCSV(filteredServices);
      downloadCSV(csvData as unknown as unknown[], `coupon_menu_${new Date().toISOString().split('T')[0]}.csv`);
    }
  };

  /**
   * Handle save service
   */
  const handleSaveService = () => {
    const validationResult = validateServiceData(formData);
    if (!validationResult.isValid) {
      setErrors(validationResult.errors);
      return;
    }

    const serviceData: Service = {
      id: editingService?.id || `service_${Date.now()}`,
      type: menuType,
      name: formData.name || '',
      description: formData.description || '',
      categoryId: formData.categoryId || 'other',
      searchCategoryId: formData.searchCategoryId || '',
      price: formData.price || 0,
      basePrice: formData.price || 0,
      taxRate: 0.1,
      priceDisplay: formData.priceDisplay || 'normal',
      originalPrice: formData.originalPrice || 0,
      discountRate: formData.discountRate || 0,
      discountValue: formData.discountValue || 0,
      duration: formData.duration || 60,
      isActive: formData.isActive !== false,
      displayOrder: 0,
      availableStaff: formData.availableStaff || [],
      requiredSkillLevel: 'beginner',
      advanceBookingDays: 30,
      minBookingNotice: 60,
      bufferTime: 15,
      options: [],
      requiredResources: [],
      tags: formData.tags || [],
      popularity: 50,
      seasonality: 'all',
      targetGender: 'unisex',
      statistics: {
        totalBookings: 0,
        revenue: 0,
        averageRating: 0,
        cancellationRate: 0,
        noShowRate: 0
      },
      createdAt: editingService?.createdAt || new Date(),
      updatedAt: new Date(),
    };

    const updatedServices = editingService
      ? services.map(s => s.id === editingService.id ? serviceData : s)
      : [...services, serviceData];

    setServices(updatedServices);
    updateSettings('services', updatedServices);
    onServiceUpdate?.(updatedServices);
    handleCloseDialog();
  };

  /**
   * Handle delete service
   */
  const handleDeleteService = () => {
    if (!editingService) return;

    const updatedServices = services.filter(s => s.id !== editingService.id);
    setServices(updatedServices);
    updateSettings('services', updatedServices);
    onServiceUpdate?.(updatedServices);
    setDeleteDialogOpen(false);
    setEditingService(null);
  };

  /**
   * Handle open dialog
   */
  const handleOpenDialog = (service?: Service) => {
    setEditingService(service || null);
    setFormData(service ? { ...service } : { type: menuType });
    setErrors([]);
    setDialogOpen(true);
  };

  /**
   * Handle close dialog
   */
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingService(null);
    setFormData({});
    setErrors([]);
  };

  /**
   * Validate service data
   */
  const validateServiceData = (data: ServiceFormData): ServiceValidationResult => {
    const errors: string[] = [];

    if (!data.name?.trim()) {
      errors.push('サービス名は必須です');
    }
    if (!data.price || data.price < 0) {
      errors.push('価格は0円以上で入力してください');
    }
    if (!data.duration || data.duration < 1) {
      errors.push('施術時間は1分以上で入力してください');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  // ==========================================
  // Derived Data
  // ==========================================

  const filteredServices = services.filter(service => service.type === menuType);

  // ==========================================
  // Render
  // ==========================================

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">サービス管理</Typography>
        
        {/* Menu Type Toggle */}
        <ToggleButtonGroup
          value={menuType}
          exclusive
          onChange={(_, value) => value && setMenuType(value)}
          size="small"
        >
          <ToggleButton value="normal">
            <TimeIcon sx={{ mr: 1 }} />
            通常メニュー
          </ToggleButton>
          <ToggleButton value="coupon">
            <CouponIcon sx={{ mr: 1 }} />
            クーポンメニュー
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          新規追加
        </Button>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setUploadDialogOpen(true)}
        >
          CSVアップロード
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadData}
        >
          CSVダウンロード
        </Button>
        <Button
          variant="outlined"
          startIcon={<TemplateIcon />}
          onClick={handleDownloadTemplate}
        >
          テンプレート
        </Button>
      </Box>

      {/* Service Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>サービス名</TableCell>
                <TableCell>カテゴリ</TableCell>
                <TableCell>価格</TableCell>
                <TableCell>時間</TableCell>
                <TableCell>状態</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredServices.map((service) => (
                <TableRow key={service.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {service.name}
                    </Typography>
                    {service.description && (
                      <Typography variant="caption" color="text.secondary">
                        {service.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={SERVICE_CATEGORIES.find(cat => cat.id === service.categoryId)?.name || 'その他'}
                      sx={{
                        backgroundColor: SERVICE_CATEGORIES.find(cat => cat.id === service.categoryId)?.color || '#757575',
                        color: 'white',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {service.type === 'coupon' && service.originalPrice ? (
                      <Box>
                        <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                          ¥{service.originalPrice.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="error.main" fontWeight="bold">
                          ¥{(service.price || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2">
                        ¥{(service.price || 0).toLocaleString()}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{service.duration}分</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={service.isActive ? '有効' : '無効'}
                      color={service.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(service)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingService(service);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredServices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      {menuType === 'normal' ? '通常メニューが登録されていません' : 'クーポンメニューが登録されていません'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Service Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingService ? 'サービス編集' : 'サービス新規追加'}
        </DialogTitle>
        <DialogContent>
          {errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="サービス名"
              value={formData.name || ''}
              onChange={(e: ReactChangeEvent) => handleFieldChange('name', e.target.value)}
              required
              fullWidth
            />
            
            <TextField
              label="説明"
              value={formData.description || ''}
              onChange={(e: ReactChangeEvent) => handleFieldChange('description', e.target.value)}
              multiline
              rows={2}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>カテゴリ</InputLabel>
              <Select
                value={formData.categoryId || ''}
                onChange={(e) => handleFieldChange('categoryId', e.target.value)}
                label="カテゴリ"
              >
                {SERVICE_CATEGORIES.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="価格"
              type="number"
              value={formData.price || ''}
              onChange={(e: ReactChangeEvent) => handleFieldChange('price', Number(e.target.value))}
              required
              fullWidth
              InputProps={{ startAdornment: '¥' }}
            />

            {menuType === 'coupon' && (
              <TextField
                label="元価格"
                type="number"
                value={formData.originalPrice || ''}
                onChange={(e: ReactChangeEvent) => handleFieldChange('originalPrice', Number(e.target.value))}
                fullWidth
                InputProps={{ startAdornment: '¥' }}
              />
            )}

            <FormControl fullWidth>
              <InputLabel>施術時間</InputLabel>
              <Select
                value={formData.duration || ''}
                onChange={(e) => handleFieldChange('duration', e.target.value)}
                label="施術時間"
              >
                {DURATION_OPTIONS.map(duration => (
                  <MenuItem key={duration} value={duration}>
                    {duration}分
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive !== false}
                  onChange={(e) => handleFieldChange('isActive', e.target.checked)}
                />
              }
              label="有効"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSaveService} variant="contained">
            {editingService ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>サービス削除</DialogTitle>
        <DialogContent>
          <Typography>
            「{editingService?.name}」を削除してもよろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleDeleteService} color="error" variant="contained">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>CSVアップロード</DialogTitle>
        <DialogContent>
          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationErrors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </Alert>
          )}
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            {menuType === 'normal' ? '通常メニュー' : 'クーポンメニュー'}のCSVファイルをアップロードします。
          </Typography>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            fullWidth
          >
            {loading ? 'アップロード中...' : 'ファイルを選択'}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

SimplifiedServiceManagementTab.displayName = 'SimplifiedServiceManagementTab';

export default SimplifiedServiceManagementTab;